import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, CheckCircle, XCircle } from 'lucide-react'

interface QuizQuestion {
  id: string
  category: 'water' | 'climate' | 'life' | 'geology'
  question: string
  options: string[]
  correctAnswer: number
  explanation: string
}

interface QuizModalProps {
  isOpen: boolean
  onClose: () => void
  onAnswer: (isCorrect: boolean, category: string) => void
}

const QUIZ_QUESTIONS: QuizQuestion[] = [
  // 水循環
  {
    id: 'water1',
    category: 'water',
    question: '地球の水循環で最も重要な役割を果たすのは？',
    options: ['海洋', '河川', '地下水', '氷河'],
    correctAnswer: 0,
    explanation: '海洋は地球の表面の約71%を占め、水循環の源となっています。'
  },
  {
    id: 'water2',
    category: 'water',
    question: '雨が降る仕組みで正しいのは？',
    options: ['雲が重くなる', '水蒸気が冷えて水滴になる', '風が強くなる', '気温が上がる'],
    correctAnswer: 1,
    explanation: '水蒸気が上昇し冷却されることで凝結し、雨となって降ります。'
  },
  
  // 気候変動
  {
    id: 'climate1',
    category: 'climate',
    question: '温室効果ガスの主な成分は？',
    options: ['酸素', '窒素', '二酸化炭素', 'アルゴン'],
    correctAnswer: 2,
    explanation: '二酸化炭素は最も主要な温室効果ガスで、地球温暖化の主因です。'
  },
  {
    id: 'climate2',
    category: 'climate',
    question: '地球温暖化の影響として正しいのは？',
    options: ['海面下降', '氷河拡大', '海面上昇', '寒冷化'],
    correctAnswer: 2,
    explanation: '温暖化により氷河が溶け、海水が膨張することで海面が上昇します。'
  },
  
  // 生態系
  {
    id: 'life1',
    category: 'life',
    question: '食物連鎖の基盤となるのは？',
    options: ['肉食動物', '草食動物', '植物（生産者）', '分解者'],
    correctAnswer: 2,
    explanation: '植物は光合成により有機物を作り、すべての生命の基盤となります。'
  },
  {
    id: 'life2',
    category: 'life',
    question: '生物多様性が重要な理由は？',
    options: ['見た目が美しい', '生態系の安定性', '観光資源', '研究対象'],
    correctAnswer: 1,
    explanation: '生物多様性は生態系のバランスを保ち、環境変化への対応力を高めます。'
  },
  
  // 地質学
  {
    id: 'geology1',
    category: 'geology',
    question: '火山噴火の主な原因は？',
    options: ['地震', 'マグマの上昇', '気候変動', '潮汐力'],
    correctAnswer: 1,
    explanation: '地下のマグマが地表に向かって上昇することで火山噴火が起こります。'
  },
  {
    id: 'geology2',
    category: 'geology',
    question: 'プレートテクトニクスとは？',
    options: ['火山活動', '地殻の移動', '氷河の動き', '海流の変化'],
    correctAnswer: 1,
    explanation: '地球の表面を覆うプレートが動くことで地震や火山活動が起こります。'
  }
]

export function QuizModal({ isOpen, onClose, onAnswer }: QuizModalProps) {
  const [currentQuestion, setCurrentQuestion] = useState<QuizQuestion | null>(null)
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null)
  const [showResult, setShowResult] = useState(false)
  const [isCorrect, setIsCorrect] = useState(false)

  useEffect(() => {
    if (isOpen) {
      // ランダムに問題を選択
      const randomQuestion = QUIZ_QUESTIONS[Math.floor(Math.random() * QUIZ_QUESTIONS.length)]
      setCurrentQuestion(randomQuestion)
      setSelectedAnswer(null)
      setShowResult(false)
    }
  }, [isOpen])

  const handleAnswerSelect = (answerIndex: number) => {
    if (showResult) return
    
    setSelectedAnswer(answerIndex)
    const correct = answerIndex === currentQuestion?.correctAnswer
    setIsCorrect(correct)
    setShowResult(true)

    // 2秒後に結果をゲームに反映
    setTimeout(() => {
      if (currentQuestion) {
        onAnswer(correct, currentQuestion.category)
        onClose()
      }
    }, 2000)
  }

  if (!isOpen || !currentQuestion) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 flex items-center justify-center pointer-events-auto z-50"
      >
        <motion.div
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 20 }}
          className="glass-panel p-8 max-w-2xl mx-4 relative"
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-white"
          >
            <X className="w-6 h-6" />
          </button>

          <div className="mb-6">
            <div className="flex items-center space-x-2 mb-4">
              <div className={`px-3 py-1 rounded-full text-sm ${
                currentQuestion.category === 'water' ? 'bg-blue-500' :
                currentQuestion.category === 'climate' ? 'bg-green-500' :
                currentQuestion.category === 'life' ? 'bg-purple-500' :
                'bg-orange-500'
              }`}>
                {currentQuestion.category === 'water' ? '🌊 水循環' :
                 currentQuestion.category === 'climate' ? '🌡️ 気候' :
                 currentQuestion.category === 'life' ? '🌱 生態系' :
                 '🌋 地質学'}
              </div>
            </div>
            
            <h2 className="text-2xl font-bold text-white mb-4">
              {currentQuestion.question}
            </h2>
          </div>

          <div className="space-y-3 mb-6">
            {currentQuestion.options.map((option, index) => {
              let buttonClass = "w-full p-4 text-left rounded-lg border-2 transition-all duration-300 "
              
              if (showResult) {
                if (index === currentQuestion.correctAnswer) {
                  buttonClass += "border-green-500 bg-green-500/20 text-green-300"
                } else if (index === selectedAnswer && index !== currentQuestion.correctAnswer) {
                  buttonClass += "border-red-500 bg-red-500/20 text-red-300"
                } else {
                  buttonClass += "border-gray-600 bg-gray-800/50 text-gray-400"
                }
              } else {
                buttonClass += "border-gray-600 bg-gray-800/50 text-gray-300 hover:border-cyan-400 hover:bg-cyan-400/10"
              }

              return (
                <button
                  key={index}
                  onClick={() => handleAnswerSelect(index)}
                  disabled={showResult}
                  className={buttonClass}
                >
                  <div className="flex items-center justify-between">
                    <span>{option}</span>
                    {showResult && index === currentQuestion.correctAnswer && (
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    )}
                    {showResult && index === selectedAnswer && index !== currentQuestion.correctAnswer && (
                      <XCircle className="w-5 h-5 text-red-400" />
                    )}
                  </div>
                </button>
              )
            })}
          </div>

          {showResult && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`p-4 rounded-lg ${
                isCorrect ? 'bg-green-500/20 border border-green-500' : 'bg-red-500/20 border border-red-500'
              }`}
            >
              <div className="flex items-center space-x-2 mb-2">
                {isCorrect ? (
                  <CheckCircle className="w-5 h-5 text-green-400" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-400" />
                )}
                <span className={`font-bold ${isCorrect ? 'text-green-300' : 'text-red-300'}`}>
                  {isCorrect ? '正解！' : '不正解...'}
                </span>
              </div>
              <p className="text-gray-300 text-sm">
                {currentQuestion.explanation}
              </p>
              <p className="text-cyan-300 text-sm mt-2">
                {isCorrect ? '地球が回復しました！' : '地球にダメージが与えられました...'}
              </p>
            </motion.div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}